import { useEffect } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { useNavigate, useParams } from 'react-router-dom';

import { useAppShell } from '../app-shell';
import { useCategoryDetail, useCategoryLeafCards } from '../../store/stash-scoped/hooks';
import { StashCardList } from '../stashboard/StashCardList';

import { CategoryHero } from './CategoryHero';
import { SubCategoryList } from './SubCategoryList';

function prefersReducedMotion(): boolean {
  return typeof window !== 'undefined'
    && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches === true;
}

/**
 * Route component for `/app/categories/:categoryId`.
 *
 * Dual-mode: when the category has direct children it renders the hero +
 * `<SubCategoryList>`; when it's a leaf it renders the same hero shell with a
 * `<StashCardList>` filtered to the leaf's cards. Tapping a sub-category row
 * recurses into the same page at the child's id.
 *
 * Pushes the breadcrumb up to the app-shell top bar (`STASH Board › {name}`)
 * and registers a Back button wired to `navigate(-1)`.
 */
export function CategoryDetailPage(): React.JSX.Element {
  const { categoryId = '' } = useParams<{ categoryId?: string }>();
  const navigate = useNavigate();

  const vm = useCategoryDetail(categoryId);
  const leafCards = useCategoryLeafCards(vm?.isLeaf ? categoryId : '');

  const { setBreadcrumb, setTopBarBack } = useAppShell();
  const parentName = vm?.parent.name;

  useEffect(() => {
    if (!parentName) return;
    setBreadcrumb(['STASH Board', parentName]);
    setTopBarBack(() => navigate(-1));
    return () => {
      setBreadcrumb([]);
      setTopBarBack(null);
    };
  }, [parentName, setBreadcrumb, setTopBarBack, navigate]);

  if (!vm) return <EmptyState />;

  const reducedMotion = prefersReducedMotion();
  const transition = {
    type: 'tween' as const,
    duration: reducedMotion ? 0 : 0.25,
    ease: 'easeInOut' as const,
  };

  return (
    <div className="mx-auto max-w-[860px] px-4 pb-24 pt-6 md:px-6 md:pt-8">
      <AnimatePresence mode="wait" initial={false}>
        <motion.div
          key={categoryId}
          initial={{ opacity: 0, y: '-30%' }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: '30%' }}
          transition={transition}
        >
          <CategoryHero
            name={vm.parent.name}
            homeBaseName={vm.parent.homeBaseName}
            {...(vm.parent.imageUrl !== undefined ? { imageUrl: vm.parent.imageUrl } : {})}
            totalCardCount={vm.parent.totalCardCount}
            subCategoryCount={vm.parent.subCategoryCount}
            friendCount={vm.parent.friendCount}
          />

          {vm.isLeaf ? (
            <div className="mt-6">
              <StashCardList
                cards={leafCards}
                onCardClick={(card) =>
                  navigate(`/app/stashcards/${card.id}`, { state: { presentation: card } })
                }
              />
            </div>
          ) : (
            <SubCategoryList
              parentName={vm.parent.name}
              subCategories={vm.subCategories}
              onSelect={(subId) => navigate(`/app/categories/${subId}`)}
            />
          )}
        </motion.div>
      </AnimatePresence>
    </div>
  );
}

function EmptyState(): React.JSX.Element {
  return (
    <div className="mx-auto max-w-[860px] px-4 pb-24 pt-6 md:px-6 md:pt-8">
      <div
        role="alert"
        className="rounded-3xl border border-destructive/40 bg-destructive/5 p-10 text-center"
      >
        <p className="text-sm font-semibold text-destructive">Couldn't find that category.</p>
        <p className="mt-2 text-xs text-muted-foreground">
          The category may have been removed, or the link is out of date.
        </p>
      </div>
    </div>
  );
}
